// Object-driven sidebar panels for the Support app.
//
// Pure JSON interpreted by the engine in ./engine — no bespoke React, and none
// should be added: if a look or behaviour can't be expressed, extend the
// engine's vocabulary once (schema + engine) and select it from JSON.
//
// Panel `id` must match the `panelId` on the matching rail item in ../apps.ts.

import {
  SIDEBAR_SCHEMA_VERSION,
  type PanelConfig,
  type RowConfig,
  type SectionConfig,
  type SidebarConfig,
} from '@clarix/shared-types';
import { SUPPORT_SETTING_SECTIONS, SUPPORT_SETTING_TABS } from '../support-settings-tabs';
import { iconNameOf } from './engine/icons';

/* ────────────────────────── Settings ────────────────────────── */

/**
 * Grouped settings tabs, generated from the SUPPORT_SETTING_TABS constant so the
 * panel and the page can't drift.
 *
 * Support has granular per-module keys and NO umbrella key, so each tab is gated
 * on its own. Gating fails closed while the matrix hydrates (support publishes
 * it on mount) — the old show-all fallback flashed every settings tab and then
 * yanked the unauthorized ones a beat later.
 */
const settingsPanel: PanelConfig = {
  id: 'support-settings',
  title: 'Settings',
  search: { placeholder: 'Search settings...' },
  defaultActiveFirst: true,
  sections: SUPPORT_SETTING_SECTIONS.map<SectionConfig>((section) => ({
    id: section,
    label: section,
    rows: SUPPORT_SETTING_TABS.filter((t) => t.section === section).map<RowConfig>((tab) => ({
      id: tab.id,
      label: tab.label,
      icon: iconNameOf(tab.icon),
      toParam: { pathname: '/support/settings', param: 'tab', value: tab.id },
      visibleWhen: { permission: tab.requiredPermission },
    })),
  })),
};

/* ────────────────────────── My Teams ────────────────────────── */

const teamsPanel: PanelConfig = {
  id: 'support-teams',
  title: 'My Teams',
  search: { placeholder: 'Search teams...' },
  // No ?team= yet means the page shows the first team — mirror that here so the
  // panel highlights what the page actually renders.
  defaultActiveFirst: true,
  sections: [
    {
      id: 'teams',
      errorText: 'Failed to load',
      searchEmptyText: 'No matching teams',
      rowsSource: { provider: 'supportTeams' },
    },
  ],
};

/* ────────────────────────── Users ────────────────────────── */

const usersPanel: PanelConfig = {
  id: 'support-users',
  title: 'Users',
  sections: [
    {
      id: 'users',
      errorText: 'Failed to load',
      rowsSource: { provider: 'supportUsers' },
    },
  ],
};

export const supportSidebar: SidebarConfig = {
  version: SIDEBAR_SCHEMA_VERSION,
  panels: [settingsPanel, teamsPanel, usersPanel],
};
